import { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Ruler, Layers } from "lucide-react";

// Local copy of the rm_name → GSM extraction used elsewhere in the codebase.
// (Pattern: 3 digits after the alpha prefix = GSM. e.g. NS12016 → 120.)
const extractGSMFromRmName = (rmName) => {
  if (!rmName) return 0;
  const match = String(rmName).match(/[A-Za-z]+(\d{3})\d{2}/);
  return match ? parseInt(match[1], 10) : 0;
};

const getInventoryGSM = (inv) => {
  if (!inv) return 0;
  // Some inventory rows store GSM in page_no directly.
  const direct = inv.page_no ? parseFloat(inv.page_no) : NaN;
  if (!isNaN(direct) && direct > 0) return direct;
  return extractGSMFromRmName(inv.rm_name);
};

/**
 * ReelSuggestionDialog
 * --------------------
 * Two-column dialog (Base / Flute) suggesting available inventory reels for
 * a selected Product/Item. Two modes:
 *   - "Inches": filter reels by reel_size matching the item's reel_size;
 *     cascade up by +10 inches only if no exact match found.
 *   - "GSM": filter reels by GSM matching ANY of the item's gsm_config GSMs.
 *
 * Selecting one Base + one Flute and clicking Apply fires `onApply(base, flute)`.
 */
const ReelSuggestionDialog = ({
  open,
  onClose,
  spec,                 // The selected ProductSpecification
  availableInventory,   // Inventory rows with status 'available'/'return'
  onApply,
}) => {
  const [mode, setMode] = useState("inches"); // "inches" | "gsm"
  const [selectedBase, setSelectedBase] = useState("");
  const [selectedFlute, setSelectedFlute] = useState("");

  // Reset selections every time the dialog re-opens for a fresh item
  useEffect(() => {
    if (open) {
      setSelectedBase("");
      setSelectedFlute("");
      setMode("inches");
    }
  }, [open, spec?.product_id]);

  const itemReelSize = useMemo(() => {
    const v = parseFloat(spec?.reel_size);
    return isNaN(v) ? 0 : v;
  }, [spec]);

  // All unique GSMs implied by the spec's gsm_config (or its color/colors hint).
  const itemGSMs = useMemo(() => {
    const set = new Set();
    (spec?.gsm_config || []).forEach((row) => {
      const g = extractGSMFromRmName(row?.gsm);
      if (g > 0) set.add(g);
    });
    return set;
  }, [spec]);

  // Filtered reel list shown in BOTH columns. Same source — user just picks
  // which column each reel goes into.
  const filteredReels = useMemo(() => {
    const stock = (availableInventory || []).filter(
      (i) => i.reel_id && (i.status === "available" || i.status === "return" || !i.status)
    );

    if (mode === "inches") {
      if (itemReelSize <= 0) return stock;
      // First pass — exact match.
      const exact = stock.filter((i) => parseFloat(i.reel_size) === itemReelSize);
      if (exact.length > 0) return exact;
      // Fallback — cascade UP only, max +10 inches.
      return stock
        .filter((i) => {
          const s = parseFloat(i.reel_size);
          return !isNaN(s) && s > itemReelSize && s <= itemReelSize + 10;
        })
        .sort((a, b) => parseFloat(a.reel_size) - parseFloat(b.reel_size));
    }

    // GSM mode — both columns show reels whose GSM ∈ item GSMs.
    if (itemGSMs.size === 0) return stock;
    return stock.filter((i) => itemGSMs.has(getInventoryGSM(i)));
  }, [availableInventory, mode, itemReelSize, itemGSMs]);

  const isExactInchMatch =
    mode === "inches" &&
    itemReelSize > 0 &&
    filteredReels.some((i) => parseFloat(i.reel_size) === itemReelSize);

  const handleApply = () => {
    if (!selectedBase || !selectedFlute) return;
    const base = filteredReels.find((i) => i.reel_id === selectedBase);
    const flute = filteredReels.find((i) => i.reel_id === selectedFlute);
    onApply(base, flute);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#0B0F19] border border-emerald-500/30 text-white max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="reel-suggestion-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-emerald-300 flex items-center gap-2">
            Suggested Reels for{" "}
            <span className="text-white font-semibold">
              {spec?.product_id} — {spec?.item_name}
            </span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Pick one Base reel and one Flute reel below — they auto-fill P1 row 1
            in the Work Order. Toggle the filter to switch between matching by
            reel size or by GSM.
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex items-center gap-2 px-1">
          <Button
            size="sm"
            variant={mode === "inches" ? "default" : "outline"}
            onClick={() => setMode("inches")}
            className={
              mode === "inches"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "border-gray-600 text-gray-300"
            }
            data-testid="reel-mode-inches"
          >
            <Ruler className="h-4 w-4 mr-1" />
            By Reel Size {itemReelSize > 0 ? `(${itemReelSize}")` : ""}
          </Button>
          <Button
            size="sm"
            variant={mode === "gsm" ? "default" : "outline"}
            onClick={() => setMode("gsm")}
            className={
              mode === "gsm"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "border-gray-600 text-gray-300"
            }
            data-testid="reel-mode-gsm"
          >
            <Layers className="h-4 w-4 mr-1" />
            By GSM{" "}
            {itemGSMs.size > 0 ? `(${Array.from(itemGSMs).join(", ")})` : ""}
          </Button>

          <div className="ml-auto text-xs text-gray-400">
            {filteredReels.length} match{filteredReels.length === 1 ? "" : "es"}
            {mode === "inches" && itemReelSize > 0 && !isExactInchMatch && filteredReels.length > 0 && (
              <span className="ml-2 text-amber-300">
                no exact {itemReelSize}" — showing up to {itemReelSize + 10}"
              </span>
            )}
            {filteredReels.length === 0 && (
              <span className="ml-2 text-red-400">no matching reels in stock</span>
            )}
          </div>
        </div>

        {/* Two columns */}
        <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0 px-1 pb-2">
          {/* BASE column */}
          <ReelColumn
            title="Base Reel"
            color="emerald"
            reels={filteredReels}
            selectedId={selectedBase}
            onSelect={(id) => setSelectedBase(id)}
            disabledId={selectedFlute}
            mode={mode}
            testid="reel-base-column"
          />

          {/* FLUTE column */}
          <ReelColumn
            title="Flute Reel"
            color="cyan"
            reels={filteredReels}
            selectedId={selectedFlute}
            onSelect={(id) => setSelectedFlute(id)}
            disabledId={selectedBase}
            mode={mode}
            testid="reel-flute-column"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-700">
          <div className="text-xs text-gray-400">
            {selectedBase && selectedFlute
              ? "Click Apply to fill P1 row 1."
              : "Select one Base and one Flute reel."}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="border-gray-600 text-gray-300">
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={!selectedBase || !selectedFlute}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="reel-apply-btn"
            >
              Apply to P1
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ReelColumn = ({ title, color, reels, selectedId, onSelect, disabledId, mode, testid }) => {
  const borderClass =
    color === "emerald" ? "border-emerald-500/40" : "border-cyan-500/40";
  const headerClass =
    color === "emerald" ? "text-emerald-300" : "text-cyan-300";
  const selectedClass =
    color === "emerald"
      ? "bg-emerald-500/20 border-emerald-400"
      : "bg-cyan-500/20 border-cyan-400";

  return (
    <div
      className={`border ${borderClass} rounded-lg bg-[#111827] flex flex-col`}
      data-testid={testid}
    >
      <div className={`px-3 py-2 border-b ${borderClass} ${headerClass} font-semibold sticky top-0 bg-[#111827] z-10`}>
        {title}
      </div>
      <div className="overflow-y-auto p-2 space-y-1.5">
        {reels.length === 0 && (
          <p className="text-gray-500 text-sm p-3 text-center">No reels match the filter.</p>
        )}
        {reels.map((r) => {
          const isSelected = r.reel_id === selectedId;
          const isDisabled = r.reel_id === disabledId;
          return (
            <button
              key={r.reel_id}
              type="button"
              disabled={isDisabled}
              onClick={() => onSelect(r.reel_id)}
              className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                isDisabled
                  ? "opacity-40 cursor-not-allowed border-gray-700 bg-gray-800/50"
                  : isSelected
                  ? `${selectedClass} cursor-pointer`
                  : "border-gray-700 hover:bg-gray-800 cursor-pointer"
              }`}
              data-testid={`${testid}-row-${r.reel_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium text-sm truncate">
                    {r.reel_id}
                    {isSelected && (
                      <Check className="inline-block h-4 w-4 ml-1 text-emerald-400" />
                    )}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {r.rm_name || "-"}
                    {mode === "gsm" && (
                      <Badge variant="outline" className="ml-2 text-[10px] py-0 px-1 border-gray-600 text-gray-300">
                        GSM {getInventoryGSM(r) || "—"}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs whitespace-nowrap">
                  <div className="text-gray-200">
                    {r.reel_size ? `${r.reel_size}"` : "—"}
                  </div>
                  <div className="text-gray-500">
                    {r.reel_weight ? `${r.reel_weight}kg` : "—"}
                  </div>
                </div>
              </div>
              {isDisabled && (
                <div className="text-[10px] text-amber-300 mt-1">
                  already picked in the other column
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReelSuggestionDialog;
